/**
 * TenantTeamView — `/settings/team`
 *
 * Gestión del equipo del tenant: invitar/revocar usuarios y ver
 * miembros activos. Solo `tenant_admin` puede operar; el resto del
 * equipo recibe la pantalla en modo read-only (banner de aviso).
 *
 * Composición:
 *   - Header con eyebrow + título + subtitle.
 *   - Sección "Miembros activos": tabla con nombre, email, tipo de
 *     cuenta (i18n), estado, fecha de alta. Consume
 *     GET /api/v1/tenants/{id}/users.
 *   - Sección "Invitaciones pendientes": tabla con email, rol,
 *     expiración, botón Revocar. Consume
 *     GET /api/v1/tenants/{id}/invites?status_filter=pending.
 *   - Botón "Invitar usuario" (solo admins) abre InviteUserDialog.
 *
 * Doc 12 (Identity) — modelo de roles post-migración 0033: el rol del
 * tenant es ortogonal al rol funcional dentro del proyecto (que vive
 * en `project_members.role` y se asigna en la pestaña Equipo & RACI).
 */

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, UserPlus, X } from 'lucide-react'

import { ApiError, isForbiddenTier } from '../../api/client'
import { retryUnlessForbiddenTier } from '../../api/queryRetry'
import {
  listInvites,
  revokeInvite,
  type InviteRead,
} from '../../api/invites'
import type { UserTenantRole } from '../../api/me'
import {
  canManageTenant,
  isAdmin as isAdminTier,
  isExternal as isExternalTier,
  tierBadgeTone,
  tierLabelKey,
} from '../../lib/tiers'
import {
  listTenantUsers,
  updateTenantUser,
  type TenantUserSummary,
} from '../../api/tenants'
import { useAccessToken } from '../../auth/useAccessToken'
import { useMe } from '../../auth/useMe'
import { toIntlLocale } from '../../i18n/config'
import { ChangeTierDialog } from '../../components/team/ChangeTierDialog'
import { InviteUserDialog } from '../../components/team/InviteUserDialog'
import { ScrollPage } from '../../components/shell/ScrollPage'
import { NoAccessView } from '../NoAccessView'
import { pushToast } from '../../stores/toastStore'

export function TenantTeamView() {
  const { t, i18n } = useTranslation(['team', 'common'])
  const { me } = useMe()
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const tenantId = me?.tenant?.id ?? null
  const isAdmin = isAdminTier(me?.role)
  // Ver invitaciones es de admin; invitar, revocar o cambiar tier además
  // exige que la organización permita escribir (OT-0073 §4.4).
  const canManage = canManageTenant(me?.role, me?.tenant?.access)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [tierChangeTarget, setTierChangeTarget] = useState<TenantUserSummary | null>(null)

  const membersQuery = useQuery({
    queryKey: ['tenant-users', tenantId, 'active'],
    queryFn: () => {
      if (!accessToken || !tenantId) throw new Error('no_token_or_tenant')
      return listTenantUsers(accessToken, tenantId, { user_status: 'active' })
    },
    enabled: !!accessToken && !!tenantId,
    retry: retryUnlessForbiddenTier,
  })

  const invitesQuery = useQuery({
    queryKey: ['tenant-invites', tenantId],
    queryFn: () => {
      if (!accessToken || !tenantId) throw new Error('no_token_or_tenant')
      return listInvites(accessToken, tenantId, { status: 'pending' })
    },
    enabled: !!accessToken && !!tenantId && isAdmin,
  })

  // D19 — lectura transversal. Invalida la lista al terminar en vez de
  // mutar el cache: el backend puede rechazar el cambio (tier external) y
  // un optimistic update dejaría el checkbox mintiendo hasta el refetch.
  const toggleMutation = useMutation({
    mutationFn: async (vars: { userId: string; next: boolean }) => {
      if (!accessToken || !tenantId) throw new Error('no_token_or_tenant')
      return updateTenantUser(accessToken, tenantId, vars.userId, {
        can_view_all_projects: vars.next,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] })
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError
          ? (err.body as unknown as { detail?: string } | null)?.detail || err.message
          : t('members.view_all_error')
      pushToast({ kind: 'critical', message: detail })
    },
  })

  const handleToggleViewAll = (userId: string, next: boolean) => {
    toggleMutation.mutate({ userId, next })
  }

  const revokeMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      if (!accessToken || !tenantId) throw new Error('no_token_or_tenant')
      await revokeInvite(accessToken, tenantId, inviteId)
      return inviteId
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['tenant-invites', tenantId],
      })
    },
    onError: (err) => {
      const detail =
        err instanceof ApiError
          ? (err.body as unknown as { detail?: string } | null)?.detail ||
            err.message
          : t('invite_modal.error_generic')
      pushToast({ kind: 'critical', message: detail })
    },
  })

  const handleRevoke = (invite: InviteRead) => {
    if (!window.confirm(t('invites.revoke_confirm_body'))) return
    revokeMutation.mutate(invite.id)
  }

  const dateFmt = new Intl.DateTimeFormat(toIntlLocale(i18n.language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  // La denegación del backend manda sobre cualquier dato previo en caché:
  // React Query conserva `data` cuando un refetch falla.
  if (isForbiddenTier(membersQuery.error)) return <NoAccessView />

  const members = membersQuery.data ?? []
  const invites = invitesQuery.data ?? []

  return (
    <ScrollPage>
      <div className="max-w-7xl">
      <Link
        to="/projects"
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t('page.back_to_projects')}
      </Link>

      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
            {t('page.eyebrow')}
          </p>
          <h1 className="font-display text-2xl font-semibold text-ink">
            {t('page.title')}
          </h1>
          <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
            {t('page.subtitle')}
          </p>
          {!isAdmin ? (
            <div className="mt-3 rounded-md border border-line bg-surface-muted px-3 py-2 inline-block">
              <p className="text-xs text-ink-muted">
                {t('page.read_only_warning')}
              </p>
            </div>
          ) : null}
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-canvas hover:bg-brand/90"
          >
            <UserPlus className="w-4 h-4" />
            {t('invite_modal.trigger')}
          </button>
        ) : null}
      </header>

      {/* ─── Miembros activos ─────────────────────────────────────────── */}
      <section className="mb-10">
        <SectionHeader
          title={t('members.section_title')}
          subtitle={t('members.section_subtitle')}
          count={members.length}
          countLabel={t('members.count', { count: members.length })}
        />
        {membersQuery.isLoading ? (
          <p className="text-sm text-ink-muted py-4">
            {t('common:actions.loading')}
          </p>
        ) : members.length === 0 ? (
          <EmptyRow message={t('members.empty')} />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-subtle">
                <tr>
                  <Th>{t('members.column_name')}</Th>
                  <Th>{t('members.column_email')}</Th>
                  <Th>{t('members.column_role')}</Th>
                  <Th>{t('members.column_status')}</Th>
                  <Th>{t('members.column_view_all')}</Th>
                  <Th>{t('members.column_joined')}</Th>
                  {canManage ? (
                    <Th className="text-right">{t('members.column_actions')}</Th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {members.map((u) => (
                  <MemberRow
                    key={u.id}
                    user={u}
                    isYou={u.id === me?.id}
                    canEdit={canManage}
                    onToggleViewAll={handleToggleViewAll}
                    onChangeTier={setTierChangeTarget}
                    pendingUserId={toggleMutation.variables?.userId ?? null}
                    dateFmt={dateFmt}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── Invitaciones pendientes (solo admins) ────────────────────── */}
      {isAdmin ? (
        <section>
          <SectionHeader
            title={t('invites.section_title')}
            subtitle={t('invites.section_subtitle')}
            count={invites.length}
            countLabel={t('invites.count', { count: invites.length })}
          />
          {invitesQuery.isLoading ? (
            <p className="text-sm text-ink-muted py-4">
              {t('common:actions.loading')}
            </p>
          ) : invites.length === 0 ? (
            <EmptyRow message={t('invites.empty')} />
          ) : (
            <div className="overflow-x-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <thead className="bg-surface-muted text-[11px] uppercase tracking-wider text-ink-subtle">
                  <tr>
                    <Th>{t('invites.column_email')}</Th>
                    <Th>{t('invites.column_role')}</Th>
                    <Th>{t('invites.column_expires')}</Th>
                    {canManage ? (
                      <Th className="w-24 text-right">
                        {t('invites.column_actions')}
                      </Th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr
                      key={inv.id}
                      className="border-t border-line hover:bg-surface-muted/40"
                    >
                      <Td className="font-mono">{inv.email}</Td>
                      <Td>
                        <RoleBadge role={inv.role} />
                      </Td>
                      <Td className="text-ink-muted">
                        {dateFmt.format(new Date(inv.expires_at))}
                      </Td>
                      {canManage ? (
                        <Td className="text-right">
                          <button
                            type="button"
                            onClick={() => handleRevoke(inv)}
                            disabled={
                              revokeMutation.isPending &&
                              revokeMutation.variables === inv.id
                            }
                            className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-muted hover:text-critical hover:border-critical/40 disabled:opacity-50"
                          >
                            <X className="w-3 h-3" />
                            {t('invites.revoke')}
                          </button>
                        </Td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {tenantId ? (
        <InviteUserDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          tenantId={tenantId}
        />
      ) : null}

      {tenantId && tierChangeTarget ? (
        <ChangeTierDialog
          open={!!tierChangeTarget}
          onClose={() => setTierChangeTarget(null)}
          tenantId={tenantId}
          user={tierChangeTarget}
        />
      ) : null}
      </div>
    </ScrollPage>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────
function SectionHeader({
  title,
  subtitle,
  count,
  countLabel,
}: {
  title: string
  subtitle: string
  count: number
  countLabel: string
}) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <div>
        <h2 className="font-display text-base font-semibold text-ink">
          {title}
        </h2>
        <p className="text-xs text-ink-muted">{subtitle}</p>
      </div>
      <span className="text-[11px] text-ink-subtle font-mono">
        {count > 0 ? countLabel : null}
      </span>
    </div>
  )
}

function EmptyRow({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed border-line bg-surface-muted/30 px-4 py-6 text-center">
      <p className="text-sm text-ink-muted">{message}</p>
    </div>
  )
}

function MemberRow({
  user,
  isYou,
  canEdit,
  onToggleViewAll,
  onChangeTier,
  pendingUserId,
  dateFmt,
}: {
  user: TenantUserSummary
  isYou: boolean
  canEdit: boolean
  onToggleViewAll: (userId: string, next: boolean) => void
  onChangeTier: (user: TenantUserSummary) => void
  pendingUserId: string | null
  dateFmt: Intl.DateTimeFormat
}) {
  const { t } = useTranslation('team')
  return (
    <tr className="border-t border-line hover:bg-surface-muted/40">
      <Td>
        <span className="text-ink">{user.display_name}</span>
        {isYou ? (
          <span className="ml-1.5 text-[10px] uppercase tracking-wider text-ink-subtle">
            {t('members.you_label')}
          </span>
        ) : null}
      </Td>
      <Td className="font-mono text-ink-muted">{user.email}</Td>
      <Td>
        <RoleBadge role={user.role} />
      </Td>
      <Td>
        <StatusBadge status={user.status} />
      </Td>
      <Td>
        {/* D19 — prohibido para tier `external`: un externo con
            visibilidad global rompería la frontera que sostiene el
            modelo de licencias. Se muestra deshabilitado con el motivo
            en vez de ocultarlo, para que se entienda por qué. */}
        <input
          type="checkbox"
          checked={user.can_view_all_projects}
          disabled={!canEdit || isExternalTier(user.role) || pendingUserId === user.id}
          onChange={(e) => onToggleViewAll(user.id, e.target.checked)}
          title={
            isExternalTier(user.role)
              ? t('members.view_all_not_for_external')
              : t('members.view_all_help')
          }
          aria-label={t('members.column_view_all')}
          className="accent-brand disabled:opacity-40 disabled:cursor-not-allowed"
        />
      </Td>
      <Td className="text-ink-muted">
        {dateFmt.format(new Date(user.created_at))}
      </Td>
      {canEdit ? (
        <Td className="text-right">
          <button
            type="button"
            onClick={() => onChangeTier(user)}
            className="rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-muted hover:text-ink hover:border-ink/30"
          >
            {t('members.change_tier_action')}
          </button>
        </Td>
      ) : null}
    </tr>
  )
}

function RoleBadge({ role }: { role: UserTenantRole }) {
  const { t } = useTranslation('team')
  const tone = tierBadgeTone(role)
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${tone}`}
    >
      {t(tierLabelKey(role))}
    </span>
  )
}

function StatusBadge({
  status,
}: {
  status: TenantUserSummary['status']
}) {
  const { t } = useTranslation('team')
  const label =
    status === 'active'
      ? t('members.status_active')
      : status === 'pending'
        ? t('members.status_pending')
        : t('members.status_disabled')
  const tone =
    status === 'active'
      ? 'text-ink'
      : status === 'pending'
        ? 'text-review'
        : 'text-ink-muted'
  return <span className={`text-xs ${tone}`}>{label}</span>
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <th className={`text-left font-medium px-3 py-2 ${className ?? ''}`}>
      {children}
    </th>
  )
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <td className={`px-3 py-2 align-middle ${className ?? ''}`}>{children}</td>
}
